import { useId } from 'vue';
import { storeToRefs } from 'pinia';

import { useSlideoutStore } from '~/stores/useSlideout';
import type { BasicSlideoutType } from '~/stores/useSlideout';

/** Represents the returned instance of the `useSlideoutStore`. */
type SlideoutStore = ReturnType<typeof useSlideoutStore>;
/** Represents the reactive state and getters extracted from the store via `storeToRefs`. */
type SlideoutRefs = ReturnType<typeof storeToRefs<SlideoutStore>>;

/**
 * @description Defines the shape of the object returned by the `useSlideout` composable.
 *
 * This type combines the reactive state/getters (`slideout`, `isSlideoutAvailable`)
 * with the store's methods, creating a complete, type-safe contract for the
 * slideout functionality.
 */
export type SlideoutComposable = {
  slideout: SlideoutRefs['currentSlideout'];
  isSlideoutAvailable: SlideoutRefs['isAvailable'];
  addSlideout: (slideout: BasicSlideoutType) => void;
  showSlideout: (slideout: BasicSlideoutType) => void;
} & Pick<SlideoutStore, 'backSlideout' | 'clearSlideoutHistory' | 'closeSlideout'>;

/**
 * @description Composable for interacting with the local slideout store.
 *
 * This composable directly accesses the `useSlideoutStore` (Pinia store) to manage slideout states.
 * It enhances the store's functionality by generating unique slideout IDs for the current component instance,
 * preventing ID collisions when multiple instances of the same component attempt to open slideouts.
 *
 * @returns {SlideoutComposable} An object containing the slideout  getters and action methods
 * with `addSlideout` and `showSlideout` methods overridden to automatically handle unique ID generation.
 */
export function useSlideout(): SlideoutComposable {
  /**
   * Generates a unique ID for the current component instance using Vue's `useId`.
   * This is used to namespace slideout IDs, preventing collisions when multiple
   * instances of the same component coexist on a page.
   */
  const randomUUID = useId();

  // Get the global slideout store instance.
  const store = useSlideoutStore();

  /**
   * Destructures action methods from the store. These methods are functions and do not need to be reactive,
   * hence they can be directly extracted from the store instance without `storeToRefs`.
   */
  const { showSlideout: externalShowSlideout, addSlideout: externalAddSlideout, backSlideout, clearSlideoutHistory, closeSlideout } = store;

  /**
   * Uses `storeToRefs` to extract reactive references for state properties and getters from the store.
   * This ensures that any consuming component remains reactive to changes in `currentSlideout` and `isAvailable`.
   */
  const { currentSlideout, isAvailable } = storeToRefs(store);

  /**
   * Appends the component-instance-specific unique ID to a slideout's base ID.
   * @param {string} slideoutId The base ID of the slideout.
   * @returns {string} A guaranteed unique ID for the slideout instance.
   */
  const getSlideoutUUID = (slideoutId: string): string => `${slideoutId}--${randomUUID}`;

  /**
   * Wraps the original `addSlideout` method to automatically apply the unique instance ID.
   * @param {BasicSlideoutType} slideout The slideout object to add.
   */
  function addSlideout(slideout: BasicSlideoutType) {
    externalAddSlideout(getSlideoutUUID(slideout.id), slideout);
  }

  /**
   * Wraps the original `showSlideout` method to automatically apply the unique instance ID.
   * @param {BasicSlideoutType} slideout The slideout object to show.
   */
  function showSlideout(slideout: BasicSlideoutType) {
    externalShowSlideout(getSlideoutUUID(slideout.id), slideout);
  }

  return {
    slideout: currentSlideout,
    isSlideoutAvailable: isAvailable,

    backSlideout,
    clearSlideoutHistory,
    closeSlideout,
    addSlideout,
    showSlideout,
  };
}
